import React, { useState } from 'react';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { fahrtenService } from '../../../services/fahrtenService';
import EditFahrtModal from './EditFahrtModal';

const FahrtennachweisTable = ({
  fahrten = [],
  loading = false,
  kennzeichenPrefix = '',
  licensePlateSuffix = '',
  onLicensePlateChange,
  onFahrtUpdated,
  onFahrtDeleted,
  currentPage = 1,
  totalPages = 1,
  totalItems = 0,
  onPageChange,
  onPreviousPage,
  onNextPage
}) => {
  const [selectedFahrt, setSelectedFahrt] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const formatDate = (d) => (d ? format(new Date(d), 'dd.MM.yyyy', { locale: de }) : '-');
  const formatDayMonth = (d) => (d ? format(new Date(d), 'dd.MM', { locale: de }) : '-');
  const formatYear = (d) => (d ? format(new Date(d), 'yyyy', { locale: de }) : '-');
  const formatTime = (d) => (d ? format(new Date(d), 'HH:mm', { locale: de }) : '-');

  // Function to get the display label for fahrtzweck
  const getFahrtzweckLabel = (value) => {
    const fahrtzweckOptions = {
      'innen-aussenreinigung': 'Innen-aussenreinigung',
      'komplettaufbereitung': 'Komplettaufbereitung',
      'smart-repair': 'Smart-repair',
      'zurueck-gefahren-fertig-sauber': 'Zurück gefahren fertig mit Sauber machen',
      'abholung-reinigung': 'Abholung für Reinigung',
      'zurueck-gefahren-fertig-pkw': 'Zurück gefahren fertiger PKW'
    };
    return fahrtzweckOptions[value] || value || '—';
  };

  const handleFahrtClick = (fahrt) => {
    setSelectedFahrt(fahrt);
    setIsEditModalOpen(true);
  };

  const handleFahrtUpdated = (updatedFahrt) => {
    if (onFahrtUpdated) {
      onFahrtUpdated(updatedFahrt);
    }
  };

  const handleFahrtDeleted = (fahrtId) => {
    if (onFahrtDeleted) {
      onFahrtDeleted(fahrtId);
    }
  };

  const handleCloseModal = () => {
    setIsEditModalOpen(false);
    setSelectedFahrt(null);
  };

  if (loading) {
    return (
      <div className="bg-white border border-black p-6 text-center">
        <span className="text-sm">Lade Fahrten…</span>
      </div>
    );
  }

  if (!fahrten.length) {
    return (
      <div className="bg-white border border-black p-6 text-center">
        <h3 className="text-base font-medium mb-1">Keine Fahrten gefunden</h3>
        <p className="text-sm text-gray-600">Es wurden noch keine Fahrten erstellt.</p>
      </div>
    );
  }

  return (
    <div className="bg-white border border-black">
      {/* Titelzeile */}
      <div className="px-4 py-3 border-b border-black">
        <div className="text-sm font-medium text-center flex items-center justify-center gap-2">
          Fahrtennachweis für das rote Kennzeichen: <span className="font-bold">{kennzeichenPrefix}</span>
          <input
            type="text"
            className="border border-gray-300 px-2 py-1 text-xs w-20 text-center"
            placeholder="XXX"
            value={licensePlateSuffix}
            onChange={(e) => onLicensePlateChange(e.target.value)}
          />
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse table-fixed text-xs">
          <colgroup>
            <col className="w-[6%]" />
            <col className="w-[8%]" />
            <col className="w-[22%]" />
            <col className="w-[14%]" />
            <col className="w-[10%]" />
            <col className="w-[23%]" />
          </colgroup>
          <thead>
            <tr>
              <th className="border border-black px-2 py-2 text-center align-middle">
                <div>Datum</div>
              </th>

              <th className="border border-black px-2 py-2 text-center align-middle">
                <div>Uhrzeit</div>
                <div className="text-[11px] font-normal text-gray-700 mt-1 leading-tight">Beginn / Ende</div>
              </th>

              <th className="border border-black px-2 py-2 text-center align-middle">
                <div>Benutztes Fahrzeug</div>
                <div className="text-[11px] font-normal text-gray-700 mt-1 leading-tight whitespace-nowrap">
                  Fzg.klasse/Herst. – komplette Fzg.Ident.-Nr.
                </div>
              </th>

              <th className="border border-black px-2 py-2 text-center align-middle">
                <div>Fahrtstrecke</div>
                <div className="text-[11px] font-normal text-gray-700 mt-1 leading-tight">(Start – über – Ziel)</div>
              </th>

              <th className="border border-black px-2 py-2 text-center align-middle">
                <div>Fahrtzweck</div>
              </th>

              <th className="border border-black px-2 py-2 text-center align-middle">
                <div>Fahrzeugführer</div>
                <div className="text-[11px] font-normal text-gray-700 mt-1 leading-tight">Name, Vorname, Anschrift</div>
              </th>
            </tr>
          </thead>

            <tbody>
             {fahrten.map((fahrt, index) => {
               const start = fahrt.fahrt_datum + ' ' + fahrt.fahrt_uhrzeit_beginn;
               const end = fahrt.fahrt_uhrzeit_ende;
               const isEven = (index + 1) % 2 === 0;
               const rowBgClass = isEven ? 'bg-gray-100' : 'bg-white';

                             return (
                 <React.Fragment key={fahrt.id}>
                   {/* Row 1 */}
                   <tr 
                     className={`cursor-pointer bg-gray-200 transition-colors ${rowBgClass}`}
                     onClick={() => handleFahrtClick(fahrt)}
                   >
                    {/* Datum (Tag Monat) with placeholder */}
                    <td className="border border-black px-2 py-2 align-top">
                      <div className="text-[11px] text-gray-700 mb-1">Tag&nbsp;Monat</div>
                      <div className="min-h-[1.75rem] border border-black/60 px-2 py-1 text-center">
                        {formatDayMonth(fahrt.fahrt_datum)}
                      </div>
                    </td>

                    {/* Uhrzeit (Beginn) with placeholder */}
                    <td className="border border-black px-2 py-2 align-top">
                      <div className="text-[11px] text-gray-700 mb-1">Beginn</div>
                      <div className="min-h-[1.75rem] border border-black/60 px-2 py-1 text-center">
                        {fahrt.fahrt_uhrzeit_beginn || '-'}
                      </div>
                    </td>

                    {/* Benutztes Fahrzeug (rowSpan=2) */}
                    <td className="border border-black px-0 py-0 align-top" rowSpan={2}>
                      <div className="grid grid-cols-2 h-full">
                        <div className="p-2">
                          <div className="text-[11px] text-gray-700 mb-1">Fzg.klasse</div>
                          <div className="min-h-[1.75rem] border border-black/60 px-2 py-1">
                            {fahrt.vehicle_model || '-'}
                          </div>
                          <div className="text-[11px] text-gray-700 mt-2 mb-1">Hersteller</div>
                          <div className="min-h-[1.75rem] border border-black/60 px-2 py-1">
                            {fahrt.vehicle_make || '-'}
                          </div>
                        </div>
                        <div className="p-2">
                          <div className="text-[11px] text-gray-700 mb-1">Fahrzeug-Ident.-Nr.</div>
                          <div className="text-[11px] min-h-[4.25rem] border border-black/60 px-2 py-1">
                            {fahrt.vin || '-'}
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* Fahrtstrecke (rowSpan=2, narrowed + wrapping) */}
                    <td className="border border-black px-0 py-0 align-top min-w-0" rowSpan={2}>
                      <div className="grid grid-cols-2 h-full">
                        <div className="p-2 break-words">
                          <div className="text-[11px] text-gray-700 mb-1">Startziel</div>
                          <div className="min-h-[1.75rem] border border-black/60 px-2 py-1">
                            {fahrt.startziel || '-'}
                          </div>
                          <div className="text-[11px] text-gray-700 mt-2 mb-1">Endziel</div>
                          <div className="min-h-[1.75rem] border border-black/60 px-2 py-1">
                            {fahrt.endziel || '-'}
                          </div>
                        </div>
                        <div className="p-2 break-words">
                          <div className="text-[11px] text-gray-700 mb-1">über</div>
                          <div className="min-h-[4.25rem] border border-black/60 px-2 py-1">
                            {fahrt.ueber || ''}
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* Fahrtzweck (rowSpan=2) */}
                    <td className="border border-black px-2 py-2 align-top" rowSpan={2}>
                      <div className="text-[11px] text-gray-700 mb-1">Zweck der Fahrt</div>
                      <div className="min-h-[4.25rem] border border-black/60 px-2 py-1">
                        {getFahrtzweckLabel(fahrt.fahrtzweck)}
                      </div>
                    </td>

                    {/* Fahrzeugführer (1st line: name) */}
                    <td className="border border-black px-2 py-2 align-top">
                      <div className="text-[11px] text-gray-700 mb-1">Nachname, Vorname</div>
                      <div className="min-h-[1.75rem] border border-black/60 px-2 py-1">
                        {fahrt.fahrer_name || 'Nicht zugewiesen'}
                      </div>
                    </td>
                  </tr>

                                     {/* Row 2 */}
                   <tr 
                     className={`cursor-pointer bg-gray-200 transition-colors ${rowBgClass}`}
                     onClick={() => handleFahrtClick(fahrt)}
                   >
                    {/* Datum (Jahr) with placeholder */}
                    <td className="border border-black px-2 py-2 align-top">
                      <div className="text-[11px] text-gray-700 mb-1">Jahr</div>
                      <div className="min-h-[1.75rem] border border-black/60 px-2 py-1 text-center">
                        {formatYear(fahrt.fahrt_datum)}
                      </div>
                    </td>

                    {/* Uhrzeit (Ende) with placeholder */}
                    <td className="border border-black px-2 py-2 align-top">
                      <div className="text-[11px] text-gray-700 mb-1">Ende</div>
                      <div className="min-h-[1.75rem] border border-black/60 px-2 py-1 text-center">
                        {end || '-'}
                      </div>
                    </td>

                    {/* Fahrzeugführer (2nd line: address) */}
                    <td className="border border-gray-500 px-2 py-2 align-top">
                      <div className="text-[11px] text-gray-700 mb-1">
                        PLZ, Ort, Straße (bei Mitarbeitern: &quot;MA&quot;)
                      </div>
                      <div className="min-h-[1.75rem] border border-black/60 px-2 py-1">
                        {fahrt.fahrer_adresse || '—'}
                      </div>
                    </td>
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="px-4 py-3 border-t border-black bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-700">
              {currentPage === 1 
                ? `Aktuelle Seite (${fahrten.length} von ${totalItems} Fahrten)`
                : `Seite ${currentPage} (${fahrten.length} Fahrten)`
              }
            </div>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={onPreviousPage}
                disabled={currentPage === 1}
                className={`px-3 py-1 text-sm border rounded ${
                  currentPage === 1
                    ? 'bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                Zurück
              </button>
              
              <div className="flex items-center space-x-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => onPageChange(page)}
                    className={`px-3 py-1 text-sm border rounded ${
                      currentPage === page
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {page}
                  </button>
                ))}
              </div>
              
              <button
                onClick={onNextPage}
                disabled={currentPage === totalPages}
                className={`px-3 py-1 text-sm border rounded ${
                  currentPage === totalPages
                    ? 'bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                Weiter
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      <EditFahrtModal
        isOpen={isEditModalOpen}
        onClose={handleCloseModal}
        fahrt={selectedFahrt}
        onFahrtUpdated={handleFahrtUpdated}
        onFahrtDeleted={handleFahrtDeleted}
      />
    </div>
  );
};

export default FahrtennachweisTable;
